import React, { useState, useEffect } from 'react';
import { Cookie, X, Check } from 'lucide-react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const grantAllConsent = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'analytics_storage': 'granted'
      });
    }
    
    // Save consent preference
    localStorage.setItem('cookie_consent', JSON.stringify({
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted',
      timestamp: new Date().toISOString()
    }));
    
    setShowBanner(false);
  };

  const denyAllConsent = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'analytics_storage': 'denied'
      });
    }
    
    // Save consent preference
    localStorage.setItem('cookie_consent', JSON.stringify({
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      timestamp: new Date().toISOString()
    }));
    
    setShowBanner(false);
  };

  const acceptEssentialOnly = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'analytics_storage': 'granted' // Allow analytics only
      });
    }
    
    localStorage.setItem('cookie_consent', JSON.stringify({
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'granted',
      timestamp: new Date().toISOString()
    }));
    
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t-4 border-blue-600 shadow-2xl animate-slide-up">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-1">
            <Cookie className="text-blue-600" size={32} />
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              We Value Your Privacy
            </h3>
            <p className="text-sm text-gray-700 mb-4">
              We use cookies and similar technologies to enhance your browsing experience, analyze our traffic, 
              and show you personalized content and targeted ads. By clicking "Accept All", you consent to our 
              use of cookies.
            </p>

            {showDetails && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
                <h4 className="font-semibold mb-2">Cookie Categories:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                    <div>
                      <strong>Essential Cookies</strong> - Required for the website to function properly (Always Active)
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <Cookie className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                    <div>
                      <strong>Analytics Cookies</strong> - Help us understand how visitors use our website
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <Cookie className="text-purple-600 flex-shrink-0 mt-0.5" size={16} />
                    <div>
                      <strong>Advertising Cookies</strong> - Used to show you relevant ads based on your interests
                    </div>
                  </li>
                </ul>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={grantAllConsent}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold flex items-center gap-2"
              >
                <Check size={18} />
                Accept All
              </button>
              
              <button
                onClick={acceptEssentialOnly}
                className="px-6 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-semibold"
              >
                Essential Only
              </button>
              
              <button
                onClick={denyAllConsent}
                className="px-6 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold flex items-center gap-2"
              >
                <X size={18} />
                Decline All
              </button>

              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-blue-600 hover:text-blue-700 font-medium text-sm underline"
              >
                {showDetails ? 'Hide Details' : 'Customize / More Info'}
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-3">
              By using our website, you agree to our{' '}
              <a href="/privacy-policy" className="text-blue-600 hover:underline">
                Privacy Policy
              </a>
              {' '}and{' '}
              <a href="/terms" className="text-blue-600 hover:underline">
                Terms of Service
              </a>
            </p>
          </div>

          <button
            onClick={denyAllConsent}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition p-1"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
